import {
  RESERVED_PAGE_SLUGS,
  customPageSchema,
  pageSettingsSchema,
  type CustomPage,
  type PageContent,
  type PageSettings,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  Eye,
  EyeOff,
  History,
  Loader2,
  Rocket,
  RotateCcw,
  Settings2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { Builder } from '@/components/customisation/builder/builder';
import { useHistory } from '@/components/customisation/builder/history';
import { pageStatus } from '@/components/customisation/builder/page-helpers';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { pageQuery, pageRevisionsQuery, studioBase } from '@/lib/customisation/queries';
import { setPreviewMode } from '@/lib/customisation/preview';
import { errorMessage } from '@/lib/forms';
import { dateTime, timeAgo } from '@/lib/format';
import { useStudioTenant } from '../customisation';

export const Route = createFileRoute('/_app/admin/customisation/pages/$pageId')({
  component: PageEditorRoute,
});

const AUTOSAVE_MS = 1500;

type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error';

function PageEditorRoute() {
  const tenant = useStudioTenant();
  const { pageId } = Route.useParams();
  const page = useQuery(pageQuery(tenant, pageId));
  return (
    <QueryState query={page}>
      {(data) => <PageEditor key={data.id} tenant={tenant} page={data} />}
    </QueryState>
  );
}

const settingsOf = (p: CustomPage): PageSettings => ({
  title: p.title,
  slug: p.slug,
  visibility: p.visibility,
  showInNav: p.showInNav,
  navLabel: p.navLabel,
  navIcon: p.navIcon,
  navOrder: p.navOrder,
  description: p.description,
});

/**
 * One page's editor: the builder over the page's draft content with its own undo history,
 * settings in a dialog, a debounced autosave (PATCH), publish/unpublish and the revision
 * history with rollback. The page's identity is the key, so switching pages resets it all.
 */
function PageEditor({ tenant, page }: { tenant: string; page: CustomPage }) {
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const history = useHistory<PageContent>(page.draft);
  const [settings, setSettings] = useState<PageSettings>(() => settingsOf(page));
  const [status, setStatus] = useState<SaveStatus>('saved');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const pending = useRef<{ draft: PageContent; settings: PageSettings } | null>(null);
  const timer = useRef<number | null>(null);
  /** What the server holds, so an unchanged document is never saved again. */
  const lastSaved = useRef(JSON.stringify({ draft: page.draft, settings: settingsOf(page) }));

  const adopt = useCallback(
    (next: CustomPage) => {
      queryClient.setQueryData(pageQuery(tenant, next.id).queryKey, next);
    },
    [queryClient, tenant],
  );

  const { mutateAsync: saveAsync } = useMutation({
    mutationFn: (body: { draft: PageContent } & PageSettings) =>
      apiRequest(`${studioBase(tenant)}/pages/${page.id}`, {
        method: 'PATCH',
        body,
        schema: customPageSchema,
      }),
  });

  const flush = useCallback(async () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const doc = pending.current;
    if (!doc) return;
    pending.current = null;
    const serialised = JSON.stringify(doc);
    if (serialised === lastSaved.current) {
      setStatus('saved');
      return;
    }
    setStatus('saving');
    try {
      const res = await saveAsync({ draft: doc.draft, ...doc.settings });
      lastSaved.current = serialised;
      adopt(res);
      void queryClient.invalidateQueries({ queryKey: ['studio', tenant, 'pages'], exact: true });
      setStatus(pending.current ? 'dirty' : 'saved');
    } catch (err) {
      setStatus('error');
      toast.error(errorMessage(err));
      pending.current ??= doc;
    }
  }, [adopt, queryClient, saveAsync, tenant]);

  // The latest flush, for the timer below: depending on `flush` itself would reschedule a save
  // every time its identity changes, which used to re-save the page in a loop.
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  // Schedule a save whenever the content or the settings change. Unchanged documents (the
  // first render, an undo back to what was saved) are skipped by flush.
  useEffect(() => {
    const doc = { draft: history.value, settings };
    if (JSON.stringify(doc) === lastSaved.current && !pending.current) return;
    pending.current = doc;
    setStatus((s) => (s === 'saving' ? s : 'dirty'));
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flushRef.current(), AUTOSAVE_MS);
  }, [history.value, settings]);

  // Save when the editor is left.
  useEffect(() => {
    const onHide = () => {
      if (pending.current) void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      if (pending.current) void flush();
    };
  }, [flush]);

  const lifecycle = useMutation({
    mutationFn: async (action: 'publish' | 'unpublish') => {
      await flush();
      return apiRequest(`${studioBase(tenant)}/pages/${page.id}/${action}`, {
        method: 'POST',
        body: {},
        schema: customPageSchema,
      });
    },
    onSuccess: async (res, action) => {
      adopt(res);
      await queryClient.invalidateQueries({ queryKey: ['studio', tenant, 'pages'], exact: true });
      await queryClient.invalidateQueries({
        queryKey: ['studio', tenant, 'pages', page.id, 'revisions'],
      });
      await queryClient.invalidateQueries({ queryKey: ['customisation'] });
      toast.success(
        action === 'publish'
          ? `Published. Customers can open /custom/${res.slug} now.`
          : 'Unpublished. The page is a draft again.',
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const rollback = useMutation({
    mutationFn: (revision: number) =>
      apiRequest(`${studioBase(tenant)}/pages/${page.id}/revisions/${revision}/rollback`, {
        method: 'POST',
        body: {},
        schema: customPageSchema,
      }),
    onSuccess: async (res) => {
      adopt(res);
      // The server already holds this; mark it saved so the autosave doesn't send it back.
      lastSaved.current = JSON.stringify({ draft: res.draft, settings: settingsOf(res) });
      history.replace(res.draft);
      setSettings(settingsOf(res));
      await queryClient.invalidateQueries({ queryKey: ['studio', tenant, 'pages'] });
      await queryClient.invalidateQueries({ queryKey: ['customisation'] });
      toast.success('Rolled back. That revision is published again.');
      setHistoryOpen(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const preview = () => {
    void flush().then(() => {
      setPreviewMode(true);
      window.open(
        `/custom/${encodeURIComponent(settings.slug)}?preview=draft`,
        '_blank',
        'noopener',
      );
    });
  };

  const published = Boolean(page.publishedAt);
  const differs = page.hasUnpublishedChanges || status !== 'saved';
  const statusBadge = pageStatus({ publishedAt: page.publishedAt, hasUnpublishedChanges: differs });
  const SAVE: Record<SaveStatus, { label: string; icon: typeof CheckCircle2; tone: string }> = {
    saved: { label: 'Draft saved', icon: CheckCircle2, tone: 'text-success' },
    dirty: { label: 'Unsaved changes', icon: CircleAlert, tone: 'text-muted-foreground' },
    saving: { label: 'Saving…', icon: Loader2, tone: 'text-muted-foreground' },
    error: { label: 'Not saved', icon: CircleAlert, tone: 'text-destructive' },
  };
  const s = SAVE[status];

  return (
    <div className="flex min-h-[60dvh] flex-col lg:h-[calc(100dvh-9.25rem)]">
      <div className="bg-card flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2 sm:px-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/customisation/pages" search={search}>
            <ArrowLeft /> Pages
          </Link>
        </Button>
        <div className="min-w-0 leading-tight">
          <h2 className="truncate text-sm font-semibold">{settings.title}</h2>
          <p className="text-muted-foreground truncate font-mono text-xs">
            /custom/{settings.slug}
          </p>
        </div>
        <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
        <span
          className={`flex items-center gap-1.5 text-xs ${s.tone}`}
          role="status"
          aria-live="polite"
        >
          <s.icon className={`size-3.5 ${status === 'saving' ? 'animate-spin' : ''}`} aria-hidden />
          {s.label}
        </span>
        {status === 'error' && (
          <Button size="sm" variant="outline" onClick={() => void flush()}>
            Retry
          </Button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings2 /> Settings
          </Button>
          <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)}>
            <History /> History
          </Button>
          <Button size="sm" variant="outline" onClick={preview}>
            <Eye /> Preview
          </Button>
          {published && (
            <Confirm
              title="Unpublish this page?"
              description="Customers lose access to it until it is published again. The draft is kept."
              action="Unpublish"
              destructive
              onConfirm={() => lifecycle.mutate('unpublish')}
            >
              <Button size="sm" variant="ghost" disabled={lifecycle.isPending}>
                <EyeOff /> Unpublish
              </Button>
            </Confirm>
          )}
          <Button
            size="sm"
            onClick={() => lifecycle.mutate('publish')}
            disabled={lifecycle.isPending}
          >
            {lifecycle.isPending ? <Loader2 className="animate-spin" /> : <Rocket />}
            {published ? 'Publish changes' : 'Publish'}
          </Button>
        </div>
      </div>
      <Builder
        surface="page"
        resetLabel="an empty page"
        tenant={tenant}
        layout={history.value.layout}
        onChange={(next, label) => history.set({ ...history.value, layout: next }, label)}
        undo={history.undo}
        redo={history.redo}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
      />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        content={history.value}
        onSave={(nextSettings, nextContent) => {
          setSettings(nextSettings);
          if (
            nextContent.width !== history.value.width ||
            nextContent.showTitle !== history.value.showTitle
          )
            history.set(nextContent, 'Change page layout settings');
        }}
      />
      <HistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        tenant={tenant}
        page={page}
        onRollback={(rev) => rollback.mutate(rev)}
        rollingBack={rollback.isPending}
      />
    </div>
  );
}

const NAV_ICONS = pageSettingsSchema.shape.navIcon.def.innerType.options as readonly string[];

function SettingsDialog({
  open,
  onOpenChange,
  settings,
  content,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: PageSettings;
  content: PageContent;
  onSave: (settings: PageSettings, content: PageContent) => void;
}) {
  const [form, setForm] = useState<PageSettings>(settings);
  const [width, setWidth] = useState(content.width);
  const [showTitle, setShowTitle] = useState(content.showTitle);
  useEffect(() => {
    if (open) {
      setForm(settings);
      setWidth(content.width);
      setShowTitle(content.showTitle);
    }
  }, [open, settings, content]);
  const parsed = pageSettingsSchema.safeParse(form);
  const errors: Partial<Record<keyof PageSettings, string>> = {};
  if (!parsed.success)
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof PageSettings;
      errors[key] ??= issue.message;
    }
  if (!errors.slug && RESERVED_PAGE_SLUGS.has(form.slug))
    errors.slug = 'That slug is used by the panel itself.';
  const valid = Object.keys(errors).length === 0;
  const set = <K extends keyof PageSettings>(key: K, value: PageSettings[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!parsed.success || !valid) return;
            onSave(parsed.data, { ...content, width, showTitle });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Page settings</DialogTitle>
            <DialogDescription>Saved with the draft; publishing makes them live.</DialogDescription>
          </DialogHeader>
          <FormField label="Title" error={errors.title}>
            <Input
              value={form.title}
              maxLength={120}
              onChange={(e) => set('title', e.target.value)}
            />
          </FormField>
          <FormField
            label="Slug"
            error={errors.slug}
            description={`Opens at /custom/${form.slug || '…'}`}
          >
            <Input
              value={form.slug}
              maxLength={64}
              className="font-mono"
              onChange={(e) => set('slug', e.target.value.toLowerCase())}
            />
          </FormField>
          <FormField
            label="Description"
            error={errors.description}
            description="Shown under the title."
          >
            <Textarea
              value={form.description}
              maxLength={300}
              rows={2}
              onChange={(e) => set('description', e.target.value)}
            />
          </FormField>
          <FormField label="Who can open it">
            <Select
              value={form.visibility}
              onValueChange={(v) => set('visibility', v as PageSettings['visibility'])}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customers">Signed-in customers</SelectItem>
                <SelectItem value="public">Anyone (also at /p/slug)</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="page-nav">Show in the sidebar (published pages only)</Label>
            <Switch
              id="page-nav"
              checked={form.showInNav}
              onCheckedChange={(v) => set('showInNav', v)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_auto_auto]">
            <FormField
              label="Nav label"
              error={errors.navLabel}
              description="Empty uses the title."
            >
              <Input
                value={form.navLabel}
                maxLength={40}
                onChange={(e) => set('navLabel', e.target.value)}
              />
            </FormField>
            <FormField label="Nav icon">
              <Select
                value={form.navIcon}
                onValueChange={(v) => set('navIcon', v as PageSettings['navIcon'])}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NAV_ICONS.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Nav order" error={errors.navOrder}>
              <Input
                type="number"
                min={0}
                max={1000}
                className="w-24"
                value={form.navOrder}
                onChange={(e) => set('navOrder', Number(e.target.value))}
              />
            </FormField>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Width">
              <Select value={width} onValueChange={(v) => setWidth(v as PageContent['width'])}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="narrow">Narrow</SelectItem>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="wide">Wide</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <div className="flex items-center justify-between gap-3 self-end pb-2">
              <Label htmlFor="page-show-title">Show the title</Label>
              <Switch id="page-show-title" checked={showTitle} onCheckedChange={setShowTitle} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid}>
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const KIND_LABEL = {
  publish: 'Published',
  rollback: 'Rolled back',
  snapshot: 'Snapshot',
  unpublish: 'Unpublished',
} as const;

function HistoryDialog({
  open,
  onOpenChange,
  tenant,
  page,
  onRollback,
  rollingBack,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: string;
  page: CustomPage;
  onRollback: (revision: number) => void;
  rollingBack: boolean;
}) {
  const revisions = useQuery({ ...pageRevisionsQuery(tenant, page.id), enabled: open });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Publishing history</DialogTitle>
          <DialogDescription>
            Every publish keeps a copy. Rolling back makes that copy the published page again and
            replaces the draft with it.
          </DialogDescription>
        </DialogHeader>
        <QueryState query={revisions}>
          {({ revisions: list }) =>
            list.length === 0 ? (
              <p className="text-muted-foreground rounded-lg border border-dashed px-4 py-6 text-center text-sm">
                Nothing published yet.
              </p>
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto rounded-lg border">
                {list.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <div className="grid min-w-0 flex-1 leading-tight">
                      <span className="truncate font-medium">
                        v{r.version} · {KIND_LABEL[r.kind]}
                        {r.note ? ` · ${r.note}` : ''}
                      </span>
                      <span
                        className="text-muted-foreground truncate text-xs"
                        title={dateTime(r.createdAt)}
                      >
                        {timeAgo(r.createdAt)}
                        {r.author ? ` by ${r.author.username}` : ''}
                      </span>
                    </div>
                    {r.kind !== 'unpublish' && (
                      <Confirm
                        title={`Roll back to v${r.version}?`}
                        description="That revision becomes the published page and the draft is replaced with it. The current version stays in the history."
                        action="Roll back"
                        onConfirm={() => onRollback(r.id)}
                      >
                        <Button size="xs" variant="outline" disabled={rollingBack}>
                          <RotateCcw /> Roll back
                        </Button>
                      </Confirm>
                    )}
                  </li>
                ))}
              </ul>
            )
          }
        </QueryState>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
